import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  TextField
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/cartSlice';
import { productService } from '../services/productService';
import { Product } from '../types';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import AddToCartNotification from '../components/AddToCartNotification';
import { useSettings } from '../contexts/SettingsContext';
import { getLotSize } from '../utils/lotSize';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { ordersEnabled } = useSettings();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  React.useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!id) return;
        const response = await productService.getProduct(id);
        setProduct(response);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement du produit');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleQuantityChange = (value: number) => {
    if (value >= 1) {
      setQuantity(value);
    }
  };

  const getLotInfo = (price: number, quantity: number, customLotSize?: number) => {
    const lotSize = getLotSize(price, customLotSize);
    if (lotSize > 1) {
      return {
        mode: 'Par lots',
        lotSize,
        displayText: `${quantity} lot${quantity > 1 ? 's' : ''} de ${lotSize}`,
        total: price * quantity * lotSize
      };
    }
    return {
      mode: 'À l’unité',
      lotSize: 1,
      displayText: `x ${quantity}`,
      total: price * quantity
    };
  };

  const handleAddToCart = async () => {
    if (product) {
      setIsAddingToCart(true);
      setShowNotification(true);

      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        dispatch(addToCart({ product, quantity }));
        setIsAddingToCart(false);
      } catch (error) {
        console.error('Erreur lors de l\'ajout au panier:', error);
        setIsAddingToCart(false);
        setShowNotification(false);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Produit non trouvé'}</Alert>
      </Container>
    );
  }

  const images = product.images.map(image => ({
    original: image,
    thumbnail: image
  }));

  const lotInfo = getLotInfo(product.price, quantity, product.lotSize);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Grid container spacing={{ xs: 3, md: 5 }}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: '#fff',
              border: '1px solid rgba(26,20,18,0.06)',
              p: { xs: 1, md: 1.5 },
            }}
          >
            <ImageGallery
              items={images}
              showPlayButton={false}
              showFullscreenButton={true}
              showThumbnails={true}
              thumbnailPosition="bottom"
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            {product.category && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {product.category}
              </Typography>
            )}
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontSize: { xs: '1.75rem', md: '2.4rem' }, mb: 1, mt: 0.5 }}
            >
              {product.name}
            </Typography>

            <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 1.5 }}>
              {product.price.toLocaleString('fr-FR')} FCFA
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Vendu {lotInfo.mode.toLowerCase()}
              {lotInfo.lotSize > 1 ? ` — ${lotInfo.lotSize} pièces par lot` : ' à l’unité'}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
              {product.description}
            </Typography>

            {product.detailedDescription && (
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                {product.detailedDescription}
              </Typography>
            )}

            <Divider sx={{ my: 2.5 }} />

            {product.ingredients?.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Ingrédients
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {product.ingredients.map((ingredient, index) => (
                    <Chip key={index} label={ingredient} variant="outlined" size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {product.preparationTime ? (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Préparation
                </Typography>
                <Chip label={`${product.preparationTime} min`} color="primary" size="small" />
              </Box>
            ) : null}

            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 3,
                backgroundColor: '#fff',
                border: '1px solid rgba(26,20,18,0.06)',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                <IconButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                  sx={{ backgroundColor: 'rgba(227, 30, 36, 0.08)' }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                  type="number"
                  inputProps={{ min: 1 }}
                  sx={{ width: 72 }}
                  size="small"
                />
                <IconButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                  sx={{ backgroundColor: 'rgba(227, 30, 36, 0.08)' }}
                >
                  <AddIcon />
                </IconButton>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleAddToCart}
                disabled={product.stock === 0 || isAddingToCart || !ordersEnabled}
                sx={{ flexGrow: 1, py: 1.4 }}
              >
                {!ordersEnabled
                  ? 'Commandes indisponibles'
                  : product.stock > 0
                    ? `Ajouter — ${lotInfo.total.toLocaleString('fr-FR')} FCFA`
                    : 'Rupture de stock'}
              </Button>
            </Box>

            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              {lotInfo.displayText}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <AddToCartNotification
        open={showNotification}
        onClose={() => setShowNotification(false)}
        productName={product.name}
        quantity={quantity}
        loading={isAddingToCart}
      />
    </Container>
  );
};

export default ProductDetails;
